import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ScrollReveal } from '@/components/landing/ScrollReveal';
import { Search, ArrowRight, BookOpen, Shield, Building2, Lightbulb, Layers } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATS = [
  { id: '', label: 'All', icon: BookOpen },
  { id: 'Private AI Workforce', label: 'AI Workforce', icon: Layers },
  { id: 'Multi-Agent Architecture', label: 'Architecture', icon: Layers },
  { id: 'Industry Applications', label: 'Industries', icon: Building2 },
  { id: 'Governance & Control', label: 'Governance', icon: Shield },
  { id: 'Aurocle Perspective', label: 'Perspectives', icon: Lightbulb },
];
const TYPE_LABELS = { pillar: 'Foundation', industry: 'Industry', governance: 'Governance', insight: 'Insight', use_case: 'Use Case', article: 'Article' };

export default function InsightsHub() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (category) params.category = category;
    if (search) params.search = search;
    axios.get(`${API}/content`, { params }).then(r => {
      setItems(r.data.items); setTotal(r.data.total);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [category, search]);

  const featured = items.filter(i => i.type === 'pillar').slice(0, 3);
  const rest = items.filter(i => !featured.find(f => f.slug === i.slug));

  return (
    <div className="min-h-screen bg-aurocle-base text-aurocle-text font-body" data-testid="insights-hub">
      <div className="noise-overlay" aria-hidden="true" />

      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-aurocle-base/95 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="font-heading text-base tracking-[0.15em] uppercase text-aurocle-text font-medium">
            Aurocle <span className="text-aurocle-gold">Agents</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-xs uppercase tracking-wider text-aurocle-text-secondary hover:text-aurocle-gold transition-colors">Home</Link>
            <Link to="/#contact" className="btn-gold text-xs py-2 px-5" data-testid="insights-book-btn">Book Meeting</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute top-20 right-1/4 w-[400px] h-[400px] bg-aurocle-gold/5 rounded-full blur-[160px] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <ScrollReveal>
            <div className="overline mb-4">Insights & Intelligence</div>
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-medium text-aurocle-text mb-4">
              Workforce Intelligence.
            </h1>
            <p className="text-base text-aurocle-platinum-muted max-w-2xl mb-10">
              Strategic perspectives on private multi-agent workforce architecture, enterprise AI deployment, governance, and operational intelligence for serious organizations.
            </p>
          </ScrollReveal>

          <ScrollReveal delay={100}>
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center mb-8">
              <div className="relative flex-1 max-w-md">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-aurocle-text-secondary" />
                <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search insights..."
                  className="premium-input pl-9 text-sm" data-testid="insights-search" />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {CATS.map(c => (
                <button key={c.id} onClick={() => setCategory(c.id)} data-testid={`cat-${c.label.toLowerCase()}`}
                  className={`px-4 py-2 text-xs font-mono uppercase tracking-wider transition-all border ${
                    category === c.id ? 'bg-aurocle-gold/10 border-aurocle-gold/30 text-aurocle-gold' : 'border-white/8 text-aurocle-text-secondary hover:text-aurocle-text'}`}>
                  {c.label}
                </button>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Featured */}
      {featured.length > 0 && !search && !category && (
        <section className="pb-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-xs font-mono uppercase tracking-wider text-aurocle-text-secondary mb-6">Featured</div>
            <div className="grid lg:grid-cols-3 gap-4">
              {featured.map((item, i) => (
                <ScrollReveal key={item.slug} delay={i * 80}>
                  <Link to={`/insights/${item.slug}`} className="block premium-card p-8 h-full group" data-testid={`featured-${item.slug}`}>
                    <div className="relative z-10">
                      <div className="flex items-center gap-2 mb-4">
                        <span className="pill pill-gold text-[10px]">{TYPE_LABELS[item.type] || item.type}</span>
                        <span className="text-[10px] font-mono text-aurocle-text-secondary">{item.reading_time}</span>
                      </div>
                      <h2 className="font-heading text-lg font-medium text-aurocle-text mb-3 group-hover:text-aurocle-gold transition-colors">{item.title}</h2>
                      <p className="text-sm text-aurocle-platinum-muted leading-relaxed line-clamp-3">{item.hero_text}</p>
                      <div className="mt-4 flex items-center gap-2 text-xs text-aurocle-gold opacity-0 group-hover:opacity-100 transition-opacity">
                        Read more <ArrowRight size={12} />
                      </div>
                    </div>
                  </Link>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* All Content */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-xs font-mono uppercase tracking-wider text-aurocle-text-secondary mb-6">
            {search || category ? `Results (${total})` : `All Insights (${total})`}
          </div>
          {loading ? (
            <div className="text-center py-16 text-aurocle-text-secondary text-sm animate-pulse">Loading intelligence...</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {rest.map((item, i) => (
                <ScrollReveal key={item.slug} delay={i * 40}>
                  <Link to={`/insights/${item.slug}`} className="block premium-card p-6 h-full group" data-testid={`content-card-${item.slug}`}>
                    <div className="relative z-10">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="pill text-[10px]">{TYPE_LABELS[item.type] || item.type}</span>
                        {item.industry && <span className="text-[10px] font-mono text-aurocle-gold/60">{item.industry}</span>}
                      </div>
                      <h3 className="font-heading text-sm font-medium text-aurocle-text mb-2 group-hover:text-aurocle-gold transition-colors">{item.title}</h3>
                      <p className="text-xs text-aurocle-platinum-muted leading-relaxed line-clamp-2">{item.hero_text}</p>
                      <div className="mt-3 flex items-center justify-between">
                        <span className="text-[10px] font-mono text-aurocle-text-secondary">{item.reading_time}</span>
                        <ArrowRight size={10} className="text-aurocle-gold opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                  </Link>
                </ScrollReveal>
              ))}
            </div>
          )}
          {rest.length === 0 && !loading && (
            <div className="text-center py-16 text-aurocle-text-secondary text-sm">No content matches your criteria.</div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading text-2xl text-aurocle-text mb-4">Ready to explore?</h2>
          <p className="text-sm text-aurocle-platinum-muted mb-8">Discover how a private multi-agent workforce could be structured around your business.</p>
          <div className="flex items-center justify-center gap-4">
            <Link to="/#contact" className="btn-gold" data-testid="insights-cta-demo">Request Private Demo</Link>
            <Link to="/#contact" className="btn-outline" data-testid="insights-cta-meeting">Book Meeting</Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <span className="text-xs text-aurocle-text-secondary">&copy; {new Date().getFullYear()} Aurocle Agents</span>
          <Link to="/" className="text-xs text-aurocle-text-secondary hover:text-aurocle-gold transition-colors">Back to home</Link>
        </div>
      </footer>
    </div>
  );
}
